import Route from '@ember/routing/route';
import { get, set, setProperties } from '@ember/object';
import { service } from '@ember/service';
import { hash } from 'rsvp';
import moment from 'moment-timezone';
import throwUnlessTaskDidCancel from 'garaje/utils/throw-unless-task-did-cancel';
import { routeEvent } from 'garaje/utils/decorators/route';

class WorkplaceEntryRoute extends Route {
  @service abilities;
  @service state;
  @service flow;
  @service skinnyLocations;
  @service router;
  @service store;

  beforeModel() {
    if (this.abilities.cannot('visit workplace')) {
      this.router.transitionTo('unauthorized');
    }

    this.skinnyLocations.loadAllTask.perform().catch(throwUnlessTaskDidCancel);
  }

  async model(params) {
    const { currentLocation, workplaceSubscription } = this.state;
    const { blocklistContacts, idScanContacts, config } = currentLocation;

    const connectedTenants = await currentLocation.getPropertyConnections();

    const include = ['visit-approval', 'visit-approval.screener-results', 'visit-approval.screener-reviews'];
    if (connectedTenants.length) include.push('invite-multi-tenancy-visitor-notification-logs');

    const entry = await currentLocation
      .findEntry(params.entry_id, include) // eslint-disable-line camelcase
      .catch((err) => {
        if (err.errors && /404/.test(err.errors.toString())) {
          this.router.transitionTo('workplace.entries');
        } else {
          throw err;
        }
      });

    if (entry.currentFlow) {
      const hasAnyUserDocumentTemplateConfigurations = get(
        entry,
        'currentFlow.activeUserDocumentTemplateConfigurations.length',
      );

      const signInFieldPage = await get(entry, 'currentFlow.signInFieldPage');
      await signInFieldPage.signInFields;

      if (get(entry, 'currentFlow.isProtect')) {
        await this.flow.getFlowWithIncludes(get(entry, 'currentFlow.id'));
      } else if (hasAnyUserDocumentTemplateConfigurations) {
        // Fetch associated Visitor Documents only if:
        //   1. There are any relevant UserDocumentTemplateConfigurations
        //   2. The Visitor Document feature flag is enabled
        try {
          entry.visitorDocuments = await this.store.query('workplace-document', {
            include:
              'user-document-template,user-document-attachments,user-document-attachments.user-document-template-attachment',
            filter: { 'entry-id': entry.id },
          });
        } catch (_) {
          // UserDocumentTemplateConfigurations will load for everyone until
          // [BE] Jira task https://envoycom.atlassian.net/browse/VIS-4390 is completed.
          // For now, don't bomb out if the visitor-document request fails.
        }
      }
    }

    return hash({
      agreeableNdas: entry.agreeableNdas,
      entry,
      config,
      connectedTenants,
      eventReports: [],
      blocklistContacts,
      idScanContacts,
      workplaceSubscription,
    });
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    setProperties(controller, {
      model: model.entry,
      connectedTenants: model.connectedTenants,
      config: model.agreeableNdas,
      agreeableNdas: model.agreeableNdas,
      agreements: model.agreeableNdas.map((agreeableNda) => agreeableNda.agreement),
      workplaceSubscription: model.workplaceSubscription,
      eventReports: model.eventReports,
      blocklistContacts: model.blocklistContacts,
      idScanContacts: model.idScanContacts,
    });

    controller._buildUserDataChangeset();
    controller._buildStaticFields();
    controller.loadPreviousEntries.perform();
    controller.pollEventReports.cancelAll();
    controller.pollEventReports.perform();
  }

  resetController(controller) {
    set(controller, 'previousEntries', []);
    controller.pollEventReports.cancelAll();
  }

  deactivate() {
    super.deactivate(...arguments);
    this.controller.pollEventReports.cancelAll();
  }

  titleToken() {
    return `${get(this.controller, 'model.fullName')} · ${moment(get(this.controller, 'model.signInTime')).format(
      'MM-DD-YYYY',
    )}`;
  }

  @routeEvent
  routeWillChange(transition) {
    const hasUnsavedChanges = get(this.controller, 'hasChanges');

    if (hasUnsavedChanges && !window.confirm('You will lose any unsaved changes. Are you sure you want to continue?')) {
      transition.abort();

      return false;
    }

    this.controller.rollbackChanges();
    return true;
  }
}

export default WorkplaceEntryRoute;
